import React, { useState, useEffect } from "react";
import styles from "./FloatingInput.module.scss";

type FieldType =
    | "text"
    | "email"
    | "password"
    | "number"
    | "date"
    | "time"
    | "tel"
    | "checkbox"
    | "radio"
    | "textarea";

interface FloatingFieldProps {
    type: FieldType;
    value?: string | boolean;
    onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
    label: React.ReactNode;
    name?: string;
    id?: string;
    disabled?: boolean;
    required?: boolean;
    autoComplete?: string;
    textareaRows?: number;
    style?: React.CSSProperties;
    options?: { value: string; label: string }[]; // per select
    defaultValue?: string; // usato solo per select
}

const FloatingField: React.FC<FloatingFieldProps> = ({
                                                         type,
                                                         value = undefined,
                                                         onChange = undefined,
                                                         label,
                                                         name,
                                                         id,
                                                         disabled = false,
                                                         required = false,
                                                         autoComplete = "off",
                                                         textareaRows = 18,
                                                         style = undefined,
                                                         options = [],
                                                         defaultValue = "",
                                                     }) => {
    const [focused, setFocused] = useState(false);

    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = useState<string>("");
    const [internalChecked, setInternalChecked] = useState<boolean>(false);

    useEffect(() => {
        if (type === "checkbox" || type === "radio") {
            if (!isControlled) return;
            setInternalChecked(Boolean(value));
        } else {
            if (!isControlled) return;
            setInternalValue(value as string);
        }
    }, [value, type, isControlled]);

    const stringValue = isControlled ? (value as string) : internalValue;
    const labelIsActive = focused || !!stringValue;

    if (type === "checkbox" || type === "radio") {
        const checked = isControlled ? Boolean(value) : internalChecked;

        const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            if (!isControlled) {
                setInternalChecked(e.target.checked);
            }
            if (onChange) onChange(e);
        };

        return (
            <label className={styles.checkboxWrapper}>
                <input
                    type={type}
                    style={style}
                    checked={checked}
                    onChange={handleCheckboxChange}
                    name={name}
                    id={id}
                    disabled={disabled}
                    required={required}
                />
                <span>{label}</span>
            </label>
        );
    }

    const sharedProps = {
        name,
        id,
        disabled,
        required,
        onFocus: () => setFocused(true),
        onBlur: () => setFocused(false),
        className: styles.floatingInputField,
    };

    if(type === "textarea") {
        return (
            <div className={styles.floatingTextareaWrapper}>
            <textarea
                {...sharedProps}
                value={stringValue}
                style={style}
                onChange={(e) => {
                    if (!isControlled) setInternalValue(e.target.value);
                    if (onChange) onChange(e);
                }}
                autoComplete={autoComplete}
                rows={textareaRows}
            />
                <label
                    htmlFor={id || name}
                    className={`${styles.floatLabel} ${labelIsActive ? styles.active : ""}`}
                >
                    {label}
                </label>
            </div>
        )
    }

    return (
        <div className={styles.floatingInputWrapper}>
                <input
                    type={type}
                    value={stringValue}
                    style={style}
                    onChange={(e) => {
                        if (!isControlled) setInternalValue(e.target.value);
                        if (onChange) onChange(e);
                    }}
                    autoComplete={autoComplete}
                    {...sharedProps}
                />
            <label
                htmlFor={id || name}
                className={`${styles.floatLabel} ${labelIsActive ? styles.active : ""}`}
            >
                {label}
            </label>
        </div>
    );
};

export default FloatingField;
